import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export interface IMapsToken {
  /** Microsoft Entra access token for the https://atlas.microsoft.com/.default scope. */
  token: string;
  /** Which Azure Maps account the token is for -- the SDK sends it as a header alongside. */
  clientId: string;
  expiresAt: string;
}

/**
 * Entra access token for the interactive map. The Azure Maps subscription
 * key deliberately never reaches the browser -- it's the same key the other
 * apps on this auth server use for server-side geocoding, and Azure Maps
 * offers no way to restrict a key to one domain, so the backend hands out
 * an expiring read-only token instead (see HeavyDutyMachineryApi's
 * MapsController).
 *
 * Returns undefined rather than throwing when maps aren't configured on
 * this environment (503), so the caller can fall back to a list-only view
 * instead of breaking the whole page.
 */
export const getMapsToken = async (): Promise<IMapsToken | undefined> => {
  const response = await apiService<IMapsToken>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MAPS_TOKEN,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  return response.success ? response.data : undefined;
};

/**
 * Reverse-geocodes a coordinate into a human-readable place name, for the
 * "near me" flow ("Cerca de mí") -- called straight against Azure Maps'
 * own REST API from the browser (not through our backend, unlike every
 * other Maps* call) using the same anonymous Entra token the map control
 * itself gets from getMapsToken(); Azure Maps accepts that same token for
 * its other REST APIs, not just the map tiles.
 *
 * Returns undefined on any failure (no token, network error, no match) --
 * the caller falls back to showing the raw coordinates instead, per how
 * this is meant to degrade.
 */
export const reverseGeocode = async (lat: number, lng: number): Promise<string | undefined> => {
  const mapsToken = await getMapsToken().catch(() => undefined);
  if (!mapsToken) return undefined;
  try {
    const url = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&query=${lat},${lng}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${mapsToken.token}`,
        'x-ms-client-id': mapsToken.clientId,
      },
    });
    if (!response.ok) return undefined;
    const data = await response.json();
    const address = data?.addresses?.[0]?.address;
    return address?.municipality || address?.freeformAddress || undefined;
  } catch {
    return undefined;
  }
};
